/**
 * Did the chord the player just played correspond to one of the branches we offered?
 *
 * The whole answer is one line of set comparison, and that is the point. From ADR 6:
 *
 * > branch matching ignores the bass: a C minor triad played in first inversion matches a
 * > "C minor" suggestion, because they are the same set. The inversion actually played is
 * > recorded on the node, not used to reject the match.
 *
 * So: convert the suggestion's `ChordSpec` with our own `toCanonical` (never trust the
 * model's `displaySymbol` — ADR 9), and compare its `pitchClasses` to the detected set
 * with `pcSetEquals`. Order, duplicates, octave, voicing and inversion all fall out for
 * free, because a pitch-class set has none of them.
 *
 * Note that a suggestion whose bass is *not* a chord tone (a pedal, "C/D") does have that
 * bass in its `pitchClasses`, because it is sounding and the recogniser will hear it. That
 * chord genuinely is a different set from a plain C, and it is right that it does not match.
 */

import { toCanonical } from '../chord/canonical.js';
import { pcSetEquals } from '../chord/pcset.js';
import type { PitchClass } from '../chord/types.js';
import type { BranchSuggestion } from '../llm/schemas.js';
import type { DetectedChord } from '../recognition/types.js';
import type { BranchSet, MatchBranch } from './types.js';

/** The pitch classes a suggestion would sound, derived by us from its structure. */
export function suggestionPitchClasses(suggestion: BranchSuggestion): PitchClass[] {
  return toCanonical(suggestion.chord).pitchClasses;
}

/**
 * The shared core of matching: the first suggestion in the set whose derived pitch-class
 * set equals `pitchClasses`.
 *
 * Suggestions arrive ranked, and the set is searched in that order, so when two branches
 * are the same sonority described two ways the higher-ranked explanation wins.
 *
 * Used directly when the chord did not come from the recogniser — a manual correction has
 * a `CanonicalChord` but no `DetectedChord`, and it should still be credited to the branch
 * it lands on.
 */
export function findSuggestionByPitchClasses(
  pitchClasses: readonly PitchClass[],
  set: BranchSet | null | undefined,
): BranchSuggestion | null {
  if (!set || set.suggestions.length === 0) return null;
  if (pitchClasses.length === 0) return null;
  for (const suggestion of set.suggestions) {
    if (pcSetEquals(suggestionPitchClasses(suggestion), pitchClasses)) return suggestion;
  }
  return null;
}

/**
 * `matchBranch(detected, set)` — see `MatchBranch` in `types.ts`.
 *
 * A `loading` set with no suggestions yet simply does not match, which is exactly right:
 * the player got there before the model did, and the chord is appended on its own terms.
 */
export const matchBranch: MatchBranch = (detected: DetectedChord, set: BranchSet) =>
  findSuggestionByPitchClasses(detected.best.pitchClasses, set);
